/**
 * Auth Context — 登录态管理 (supports 2FA flow)
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { api, setToken, getToken, ApiRequestError } from './api';
import { clearRuntimeClientCache } from './runtime-api';
import { markMdiScopeForFreshStart, useMdiStore } from '@/stores/mdi-store';

export interface User {
  id: string;
  email: string;
  name?: string;
  tenantId: string;
  tenantSlug?: string;
  role?: string;
  /**
   * Explicit extra permission grants beyond the primary role.
   * Decoded from the JWT 'grants' claim at login time.
   * Used by Sidebar to filter category visibility.
   */
  grants?: string[];
  /**
   * Operational modules enabled for this tenant (e.g. ['TMS','FMS','WMS']).
   * Set from the login/me API response. Used by Sidebar to hide
   * module-specific categories (transport/freight/warehouse).
   */
  enabledModules?: string[];
  defaultCurrency?: string;
  countryCode?: string | null;
  avatarUrl?: string;
  /** Ownership group ID (from gid JWT claim) */
  groupId?: string;
}

interface TenantInfo {
  id: string;
  name: string;
  slug?: string;
  defaultCurrency?: string;
  default_currency?: string;
  countryCode?: string | null;
  country_code?: string | null;
}

interface LoginResult {
  requiresTwoFA?: { method: string };
  requireTenantSelection?: { tenants: TenantInfo[]; userId: string };
}

interface AuthMeResponse {
  id: string;
  email: string;
  name?: string;
  role?: string;
  grants?: string[];
  tenantId: string;
  tenant?: {
    id: string;
    name: string;
    slug?: string;
    enabledModules?: string[];
    defaultCurrency?: string;
    default_currency?: string;
    countryCode?: string | null;
    country_code?: string | null;
  };
}

interface AuthTenantsResponse {
  currentTenantId: string;
  tenants: Array<TenantInfo & {
    role?: string;
    current?: boolean;
  }>;
}

interface AuthState {
  user: User | null;
  /** All tenants the current user belongs to */
  tenants: TenantInfo[];
  loading: boolean;
  login: (email: string, password: string, tenantId?: string) => Promise<LoginResult | void>;
  logout: () => void;
  completeTwoFA: (code: string, method: string) => Promise<void>;
  /** Switch to a different tenant — re-issues JWT */
  switchTenant: (tenantId: string) => Promise<void>;
  /** Switch to a tenant within the current group using the group switch endpoint */
  switchGroupTenant: (groupId: string, tenantId: string) => Promise<void>;
  /** Direct token login (used by admin Google Sign-In) */
  loginWithToken: (token: string, userData: { id: string; email: string; name?: string; tenantId: string; tenantSlug?: string; enabledModules?: string[] }, tenantsList?: TenantInfo[]) => void;
}

const AuthContext = createContext<AuthState | null>(null);

function isInvalidSessionError(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  return (
    error.status === 401
    || error.code === 'AUTH_TOKEN_INVALID'
    || error.code === 'AUTH_TENANT_CONTEXT_INVALID'
    || error.code === 'AUTH_MISSING'
  );
}

function toUserFromAuthMe(currentUser: AuthMeResponse, fallback?: User | null): User {
  const tenant = currentUser.tenant;
  return {
    id: currentUser.id,
    email: currentUser.email,
    name: currentUser.name,
    tenantId: currentUser.tenantId,
    tenantSlug: tenant?.slug ?? fallback?.tenantSlug,
    role: currentUser.role ?? fallback?.role,
    grants: currentUser.grants ?? fallback?.grants,
    enabledModules: tenant?.enabledModules ?? fallback?.enabledModules,
    defaultCurrency: tenant?.defaultCurrency ?? tenant?.default_currency ?? fallback?.defaultCurrency,
    countryCode: tenant?.countryCode ?? tenant?.country_code ?? fallback?.countryCode,
    avatarUrl: fallback?.avatarUrl,
    groupId: fallback?.groupId,
  };
}

function mergeTenantInfoRows(current: TenantInfo[], incoming: TenantInfo[]): TenantInfo[] {
  const byId = new Map(current.map((tenant) => [tenant.id, tenant]));
  return incoming.map((tenant) => ({
    ...(byId.get(tenant.id) ?? {}),
    ...tenant,
  }));
}

/** Decode JWT payload without verification (browser-side) */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const normalized = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
    const payload = JSON.parse(atob(padded));
    return payload;
  } catch {
    return null;
  }
}

/** Restore user from JWT payload + cached profile */
function restoreUserFromToken(token: string): User | null {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.sub || !payload.tid) return null;
  // Check expiry
  if (typeof payload.exp === 'number' && payload.exp * 1000 < Date.now()) return null;
  const tokenGrants = Array.isArray(payload.grants) ? (payload.grants as string[]) : undefined;
  const tokenModules = Array.isArray((payload as Record<string, unknown>).mods)
    ? ((payload as Record<string, unknown>).mods as string[])
    : undefined;
  // Try cached profile
  const cached = localStorage.getItem('boox_user');
  if (cached) {
    try {
      const u = JSON.parse(cached) as User;
      if (u.id === payload.sub) {
        return {
          ...u,
          tenantId: payload.tid as string,
          role: (payload.role as string) ?? u.role,
          grants: tokenGrants ?? u.grants,
          enabledModules: tokenModules ?? u.enabledModules,
          groupId: typeof payload.gid === 'string' ? payload.gid : u.groupId,
        };
      }
    } catch { /* ignore */ }
  }
  return {
    id: payload.sub as string,
    email: '',
    tenantId: payload.tid as string,
    role: (payload.role as string) ?? 'viewer',
    grants: tokenGrants,
    enabledModules: tokenModules,
    groupId: typeof payload.gid === 'string' ? payload.gid : undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [tenants, setTenantsState] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);

  const setTenants = useCallback((t: TenantInfo[]) => {
    setTenantsState(t);
    localStorage.setItem('boox_tenants', JSON.stringify(t));
  }, []);

  const setUser = useCallback((u: User | null) => {
    setUserState(u);
    if (u) {
      localStorage.setItem('boox_user', JSON.stringify(u));
    } else {
      localStorage.removeItem('boox_user');
    }
  }, []);

  const refreshTenants = useCallback(async () => {
    const payload = await api.get<AuthTenantsResponse>('/auth/tenants');
    setTenantsState((current) => {
      const merged = mergeTenantInfoRows(current, payload.tenants);
      localStorage.setItem('boox_tenants', JSON.stringify(merged));
      return merged;
    });
    return payload.tenants;
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      const existing = getToken();
      if (!existing) {
        if (!cancelled) setLoading(false);
        return;
      }

      const restored = restoreUserFromToken(existing);
      if (!restored) {
        setToken(null);
        if (!cancelled) setLoading(false);
        return;
      }

      // Restore cached tenants list
      try {
        const cached = localStorage.getItem('boox_tenants');
        if (cached && !cancelled) setTenantsState(JSON.parse(cached));
      } catch { /* ignore */ }

      try {
        const currentUser = await api.get<AuthMeResponse>('/auth/me');
        if (!cancelled) {
          setUser(toUserFromAuthMe(currentUser, restored));
          await refreshTenants().catch(() => undefined);
        }
      } catch (error) {
        if (isInvalidSessionError(error)) {
          setToken(null);
          if (!cancelled) {
            setUser(null);
            setTenants([]);
            setLoading(false);
          }
          return;
        }
        if (!cancelled) setUser(restored);
      }

      if (!cancelled) setLoading(false);
    };

    void restoreSession();

    return () => {
      cancelled = true;
    };
  }, [refreshTenants, setTenants, setUser]);

  useEffect(() => {
    if (loading || !user) return;
    if (user.email && Array.isArray(user.enabledModules) && user.tenantSlug) return;

    let cancelled = false;

    const hydrateUser = async () => {
      try {
        const currentUser = await api.get<AuthMeResponse>('/auth/me');
        if (!cancelled) {
          setUser(toUserFromAuthMe(currentUser, user));
          await refreshTenants().catch(() => undefined);
        }
      } catch (error) {
        if (isInvalidSessionError(error)) {
          setToken(null);
          if (!cancelled) {
            setUser(null);
            setTenants([]);
          }
        }
      }
    };

    void hydrateUser();

    return () => {
      cancelled = true;
    };
  }, [loading, refreshTenants, setTenants, setUser, user]);

  useEffect(() => {
    if (loading) return;
    useMdiStore.getState().syncSessionScope({
      tenantId: user?.tenantId ?? null,
      userId: user?.id ?? null,
    });
  }, [loading, user?.id, user?.tenantId]);

  const login = useCallback(async (email: string, password: string, tenantId?: string): Promise<LoginResult | void> => {
    const data = await api.post<{
      accessToken: string;
      refreshToken: string;
      user: { id: string; email: string; name?: string; avatarUrl?: string };
      tenant: { id: string; name: string; slug?: string; enabledModules?: string[]; defaultCurrency?: string; default_currency?: string; countryCode?: string | null; country_code?: string | null };
      tenants?: { id: string; name: string; slug?: string }[];
      requiresTwoFA?: { method: string };
      requireTenantSelection?: boolean;
      userId?: string;
    }>('/auth/login', { email, password, ...(tenantId ? { tenantId } : {}) });

    // Multi-tenant user without preferred tenant — need to select
    if (data.requireTenantSelection && data.tenants) {
      return {
        requireTenantSelection: {
          tenants: data.tenants as TenantInfo[],
          userId: data.userId ?? '',
        },
      };
    }

    // Store token even if 2FA required (needed for MFA API calls)
    setToken(data.accessToken);
    clearRuntimeClientCache();

    // Persist tenants list from login response
    if (data.tenants) {
      setTenants(data.tenants);
    }

    if (data.requiresTwoFA) {
      // Don't set user yet — pending 2FA
      return { requiresTwoFA: data.requiresTwoFA };
    }

    setUser({
      id: data.user.id,
      email: data.user.email,
      name: data.user.name,
      tenantId: data.tenant.id,
      tenantSlug: data.tenant.slug,
      // Decode role + grants from the JWT (not from login response body)
      role: (decodeJwtPayload(data.accessToken)?.role as string) ?? 'viewer',
      grants: (() => {
        const p = decodeJwtPayload(data.accessToken);
        return Array.isArray(p?.grants) ? (p!.grants as string[]) : undefined;
      })(),
      enabledModules: data.tenant.enabledModules,
      defaultCurrency: data.tenant.defaultCurrency ?? data.tenant.default_currency,
      countryCode: data.tenant.countryCode ?? data.tenant.country_code,
      avatarUrl: data.user.avatarUrl,
      groupId: (() => { const p = decodeJwtPayload(data.accessToken); return typeof p?.gid === 'string' ? p.gid : undefined; })(),
    });
  }, [setUser, setTenants]);

  const completeTwoFA = useCallback(async (code: string, method: string) => {
    const data = await api.post<{
      accessToken: string;
      refreshToken: string;
      twofaVerified: boolean;
    }>('/auth/mfa/verify', { code, method });

    // Replace token with 2FA-verified token
    setToken(data.accessToken);
    clearRuntimeClientCache();

    // Decode user from new token
    const payload = decodeJwtPayload(data.accessToken);
    const cached = localStorage.getItem('boox_user');
    let restoredUser: User | null = null;
    if (cached) {
      try { restoredUser = JSON.parse(cached) as User; } catch { /* ignore */ }
    }

    setUser({
      id: (payload?.sub as string) ?? restoredUser?.id ?? '',
      email: restoredUser?.email ?? '',
      name: restoredUser?.name,
      tenantId: (payload?.tid as string) ?? restoredUser?.tenantId ?? '',
      tenantSlug: restoredUser?.tenantSlug,
      role: (payload?.role as string) ?? 'viewer',
      grants: Array.isArray(payload?.grants) ? (payload!.grants as string[]) : undefined,
      enabledModules: restoredUser?.enabledModules,
      defaultCurrency: restoredUser?.defaultCurrency,
      countryCode: restoredUser?.countryCode,
      avatarUrl: restoredUser?.avatarUrl,
      groupId: typeof payload?.gid === 'string' ? payload.gid : restoredUser?.groupId,
    });
  }, [setUser]);

  const switchTenant = useCallback(async (tenantId: string) => {
    const data = await api.post<{
      accessToken: string;
      refreshToken: string;
      user: { id: string; email: string; name?: string; role?: string; grants?: string[]; avatarUrl?: string };
      tenant: { id: string; name: string; slug?: string; enabledModules?: string[]; defaultCurrency?: string; default_currency?: string; countryCode?: string | null; country_code?: string | null };
    }>('/auth/switch-tenant', { tenantId });

    setToken(data.accessToken);
    clearRuntimeClientCache();
    markMdiScopeForFreshStart({
      tenantId: data.tenant.id,
      userId: data.user.id,
    });

    setUser({
      id: data.user.id,
      email: data.user.email,
      name: data.user.name,
      tenantId: data.tenant.id,
      tenantSlug: data.tenant.slug,
      role: (decodeJwtPayload(data.accessToken)?.role as string) ?? 'viewer',
      grants: (() => {
        const p = decodeJwtPayload(data.accessToken);
        return Array.isArray(p?.grants) ? (p!.grants as string[]) : undefined;
      })(),
      enabledModules: data.tenant.enabledModules,
      defaultCurrency: data.tenant.defaultCurrency ?? data.tenant.default_currency,
      countryCode: data.tenant.countryCode ?? data.tenant.country_code,
      avatarUrl: data.user.avatarUrl,
      groupId: (() => { const p = decodeJwtPayload(data.accessToken); return typeof p?.gid === 'string' ? p.gid : undefined; })(),
    });
    await refreshTenants().catch(() => undefined);
  }, [refreshTenants, setUser]);

  const switchGroupTenant = useCallback(async (groupId: string, tenantId: string) => {
    const data = await api.post<{
      accessToken: string;
      tenant: { id: string; name: string; slug?: string; entity_type?: string; country_code?: string | null; countryCode?: string | null; default_currency?: string; defaultCurrency?: string; enabledModules?: string[] };
    }>(`/groups/${groupId}/switch/${tenantId}`, {});

    setToken(data.accessToken);
    clearRuntimeClientCache();
    const payload = decodeJwtPayload(data.accessToken);

    // Restore profile from localStorage cache (always up-to-date, avoids stale closure)
    let cachedUser: User | null = null;
    try {
      const cached = localStorage.getItem('boox_user');
      if (cached) cachedUser = JSON.parse(cached) as User;
    } catch { /* ignore */ }

    markMdiScopeForFreshStart({
      tenantId: data.tenant.id,
      userId: (payload?.sub as string) ?? cachedUser?.id ?? null,
    });

    setUser({
      id: (payload?.sub as string) ?? cachedUser?.id ?? '',
      email: cachedUser?.email ?? '',
      name: cachedUser?.name,
      tenantId: data.tenant.id,
      tenantSlug: data.tenant.slug ?? cachedUser?.tenantSlug,
      role: (payload?.role as string) ?? 'viewer',
      grants: Array.isArray(payload?.grants) ? (payload!.grants as string[]) : undefined,
      enabledModules: data.tenant?.enabledModules ?? cachedUser?.enabledModules,
      defaultCurrency: data.tenant.defaultCurrency ?? data.tenant.default_currency ?? cachedUser?.defaultCurrency,
      countryCode: data.tenant.countryCode ?? data.tenant.country_code ?? cachedUser?.countryCode,
      avatarUrl: cachedUser?.avatarUrl,
      groupId: typeof payload?.gid === 'string' ? payload.gid : groupId,
    });
    await refreshTenants().catch(() => undefined);
  }, [refreshTenants, setUser]);

  const logout = useCallback(() => {
    // Clear MDI tabs on signout if user preference is set (default: true)
    const clearTabs = localStorage.getItem('boox_clear_tabs_on_signout') !== 'false';
    if (clearTabs) {
      try {
        // Clear all session-scoped MDI tab storage
        const keysToRemove: string[] = [];
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key?.startsWith('boox_mdi_tabs')) keysToRemove.push(key);
        }
        keysToRemove.forEach((k) => sessionStorage.removeItem(k));
      } catch { /* ignore */ }
    }
    setToken(null);
    clearRuntimeClientCache();
    setUser(null);
    setTenants([]);
    localStorage.removeItem('boox_user');
    localStorage.removeItem('boox_tenants');
  }, [setUser, setTenants]);

  const loginWithToken = useCallback((token: string, userData: { id: string; email: string; name?: string; tenantId: string; tenantSlug?: string; enabledModules?: string[]; defaultCurrency?: string; countryCode?: string | null }, tenantsList?: TenantInfo[]) => {
    setToken(token);
    clearRuntimeClientCache();
    const payload = decodeJwtPayload(token);
    setUser({
      id: userData.id,
      email: userData.email,
      name: userData.name,
      tenantId: userData.tenantId,
      tenantSlug: userData.tenantSlug,
      role: (payload?.role as string) ?? 'platform_admin',
      grants: Array.isArray(payload?.grants) ? (payload!.grants as string[]) : undefined,
      enabledModules: userData.enabledModules,
      defaultCurrency: userData.defaultCurrency,
      countryCode: userData.countryCode,
    });
    if (tenantsList) setTenants(tenantsList);
  }, [setUser, setTenants]);

  return (
    <AuthContext value={{ user, tenants, loading, login, logout, completeTwoFA, switchTenant, switchGroupTenant, loginWithToken }}>
      {children}
    </AuthContext>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}
